import React, { useState, useEffect, useRef } from 'react';
import {
  Input, Button, Typography, Select, Form, message, Tooltip, Popconfirm
} from 'antd';
import {
  SendOutlined, CheckCircleOutlined, UndoOutlined, DeleteOutlined, EditOutlined, MessageOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

// Helper function to render markdown links in comments
const renderMarkdownLinks = (text) => {
  if (!text) return text;
  
  // Regular expression to match markdown links [text](url)
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  
  const parts = [];
  let lastIndex = 0;
  let match;
  
  while ((match = linkRegex.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    
    // Add the link - inherit color from parent
    parts.push(
      <a 
        key={match.index}
        href={match[2]} 
        target="_blank" 
        rel="noopener noreferrer"
        style={{
          color: 'inherit',
          textDecoration: 'underline',
          cursor: 'pointer',
          fontWeight: '500'
        }}
      >
        {match[1]}
      </a>
    );
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  
  return parts.length > 0 ? parts : text;
};

/**
 * Reusable Comment System Content component
 * Extracted from CommentModal to be used inline in BugReportModal
 * Contains all the comment display and interaction logic without the modal wrapper
 */
const CommentSystemContent = ({
  selectedTripForComment,
  comments,
  newComment,
  setNewComment,
  isSavingComment,
  commentGroupResolved,
  isResolving,
  isReopening,
  showResolveModal,
  setShowResolveModal,
  selectedResolutionCode,
  setSelectedResolutionCode,
  customResolutionCode,
  setCustomResolutionCode,
  resolutionCodes,
  setResolutionCodes,
  currentThreadId,
  availableThreads,
  issueTitles,
  jiraData,
  editingTitle,
  setEditingTitle,
  editingComment,
  setEditingComment,
  resolutionComment,
  deletingCommentId,
  onSaveComment,
  onMarkAsResolved,
  onReopenGroup,
  onResolveWithCode,
  onAddResolutionCode,
  onNewThread,
  onSelectThread,
  onTitleEdit,
  onTitleKeyPress,
  onEditComment,
  onSaveEditComment,
  onCancelEditComment,
  onDeleteComment,
  onReopenCommentGroup,
  editor,
  givenName,
  familyName,
  threadType = 'support_ticket', // Default to support_ticket for backward compatibility,
  isAdminOrDev,
  onDeleteThread
}) => {
  // Local state for comment editing
  const [currentTitleEdit, setCurrentTitleEdit] = useState('');
  const [currentCommentEdit, setCurrentCommentEdit] = useState('');
  const [hoveredComment, setHoveredComment] = useState(null);

  // Helper functions - Use same pattern as OTR CommentModal
  const isAuthor = (commentAuthor) => {
    // Use exact same logic as OTR system
    const authorName = typeof commentAuthor === 'string' ? commentAuthor : commentAuthor?.name || 'Unknown';
    
    // Use editor if available, otherwise construct from givenName and familyName
    const currentUser = editor || (givenName && familyName ? `${givenName} ${familyName}` : null);
    
    const isCurrentUser = authorName === currentUser;
    
    return isCurrentUser;
  };

  // Check if current user is the author of the current thread
  const isThreadAuthor = () => {
    if (!currentThreadId || !availableThreads) return false;
    
    const thread = availableThreads.find(t => t.threadId === currentThreadId);
    if (!thread || !thread.author) return false;
    
    // Parse the thread author field (can be string or JSON stringified object)
    let threadAuthorString = null;
    try {
      const parsedAuthor = typeof thread.author === 'string' ? JSON.parse(thread.author) : thread.author;
      // Check if it's an object with email or name, or just a string
      if (parsedAuthor && typeof parsedAuthor === 'object') {
        // If it's an object, extract name or email
        if (parsedAuthor.given_name && parsedAuthor.family_name) {
          threadAuthorString = `${parsedAuthor.given_name} ${parsedAuthor.family_name}`;
        } else if (parsedAuthor.email) {
          threadAuthorString = parsedAuthor.email;
        }
      } else {
        // It's already a string
        threadAuthorString = parsedAuthor;
      }
    } catch (e) {
      // If parsing fails, treat as plain string
      threadAuthorString = thread.author;
    }
    
    if (!threadAuthorString) return false;
    
    // Get current user identifier (editor is either name or email string)
    const currentUser = editor || (givenName && familyName ? `${givenName} ${familyName}` : null);
    
    if (!currentUser) return false;
    
    // Direct string comparison (handles both name and email cases)
    return threadAuthorString === currentUser;
  };

  const formatAuthor = (author) => {
    try {
      const parsedAuthor = typeof author === 'string' ? JSON.parse(author) : author;
      return parsedAuthor?.given_name && parsedAuthor?.family_name 
        ? `${parsedAuthor.given_name} ${parsedAuthor.family_name}` 
        : parsedAuthor?.email || 'Unknown User';
    } catch (e) {
      return author || 'Unknown User';
    }
  };

  // Handler functions
  const handleEditComment = (comment) => {
    if (onEditComment) {
      onEditComment(comment);
    }
  };

  const handleDeleteComment = (commentId) => {
    if (onDeleteComment) {
      onDeleteComment(commentId);
    }
  };

  const handleSaveEditedComment = async (commentRefId) => {
    if (onSaveEditComment) {
      await onSaveEditComment(commentRefId, currentCommentEdit);
      setCurrentCommentEdit('');
    }
  };

  const handleCancelEditedComment = () => {
    if (onCancelEditComment) {
      onCancelEditComment();
    }
    setCurrentCommentEdit('');
  };
  // Handle thread selection
  const handleSelectThread = (threadId) => {
    if (onSelectThread) {
      onSelectThread(threadId);
    }
  };

  // Handle title editing
  const handleTitleEdit = () => {
    if (onTitleEdit) {
      onTitleEdit();
    }
  };

  // Handle title key press
  const handleTitleKeyPress = (e) => {
    if (onTitleKeyPress) {
      onTitleKeyPress(e);
    }
  };


  // Handle save edit comment
  const handleSaveEditComment = (commentRefId, newMessage) => {
    if (onSaveEditComment) {
      onSaveEditComment(commentRefId, newMessage);
    }
  };

  // Handle cancel edit comment
  const handleCancelEditComment = () => {
    if (onCancelEditComment) {
      onCancelEditComment();
    }
  };


  // Handle save comment
  const handleSaveComment = () => {
    if (onSaveComment && currentThreadId) {
      onSaveComment(currentThreadId, newComment);
    }
  };

  // Handle mark as resolved
  const handleMarkAsResolved = () => {
    if (onMarkAsResolved) {
      onMarkAsResolved();
    }
  };

  // Handle reopen group
  const handleReopenGroup = () => {
    if (onReopenGroup) {
      onReopenGroup();
    }
  };

  // Handle resolve with code
  const handleResolveWithCode = () => {
    if (onResolveWithCode) {
      onResolveWithCode();
    }
  };

  // Handle add resolution code
  const handleAddResolutionCode = () => {
    if (onAddResolutionCode) {
      onAddResolutionCode();
    }
  };

  // Handle new thread
  const handleNewThread = () => {
    if (onNewThread) {
      onNewThread();
    }
  };

  // Handle reopen comment group
  const handleReopenCommentGroup = () => {
    if (onReopenCommentGroup) {
      onReopenCommentGroup();
    }
  };

  return (
    <div style={{ 
      height: '100%',
      display: 'flex', 
      flexDirection: 'column', 
      background: '#ffffff',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
    }}>
      {/* Messages Header */}
      <div style={{ 
        padding: '20px 24px',
        borderBottom: '1px solid #e2e8f0',
        background: '#f8f9fa',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '16px' 
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '8px',
            background: '#e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#4a5568',
            fontSize: '16px',
            fontWeight: '600',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            {currentThreadId ? 'I' : 'R'}
          </div>
          <div>
            <div style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: '#2d3748',
              marginBottom: '4px'
            }}>
              {currentThreadId ? (
                (() => {
                  // Find the thread to get its JIRA ID
                  const thread = availableThreads.find(t => t.threadId === currentThreadId);
                  const jiraId = thread?.metaInfo?.jiraId;
                  
                  // Use JIRA summary if available, otherwise fall back to issueTitles
                  if (jiraId && jiraData && jiraData[jiraId]?.summary) {
                    return jiraData[jiraId].summary;
                  }
                  
                  return issueTitles[currentThreadId] || 'Select an issue to view comments';
                })()
              ) : 'Select an issue to view comments'}
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#6b7280' 
            }}>
              {comments.length} comment{comments.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
        
        {/* Action Buttons - Icon only, properly aligned */}
        <div style={{ 
          display: 'flex', 
          gap: '6px', 
          alignItems: 'center',
          justifyContent: 'flex-end'
        }}>
          {/* Resolution Controls - Only show for OTR issues, not support tickets */}
          {threadType !== 'support_ticket' && (
            <>
              {commentGroupResolved ? (
                <Tooltip title="Reopen issue">
                  <Button 
                    type="text"
                    size="small"
                    onClick={handleReopenGroup}
                    loading={isReopening}
                    icon={<UndoOutlined />}
                    style={{ 
                      width: '32px',
                      height: '32px',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '6px',
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      color: '#059669',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f0fdf4';
                      e.currentTarget.style.borderColor = '#10b981';
                      e.currentTarget.style.color = '#047857';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#ffffff';
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.color = '#059669';
                      e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                    }}
                  />
                </Tooltip>
              ) : (
                <Tooltip title="Resolve issue">
                  <Button 
                    type="text"
                    size="small"
                    onClick={handleMarkAsResolved}
                    icon={<CheckCircleOutlined />}
                    style={{ 
                      width: '32px',
                      height: '32px',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '6px',
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      color: '#2563eb',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#eff6ff';
                      e.currentTarget.style.borderColor = '#3b82f6';
                      e.currentTarget.style.color = '#1d4ed8';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#ffffff';
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.color = '#2563eb';
                      e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                    }}
                  />
                </Tooltip>
              )}
            </>
          )}
          
          {((isAdminOrDev || isThreadAuthor()) && onDeleteThread && currentThreadId) && (
            <Popconfirm
              title="Are you sure?"
              description="This will archive the issue. You can restore it later if needed."
              onConfirm={() => onDeleteThread(currentThreadId)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Delete issue">
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  style={{
                    width: '32px',
                    height: '32px',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '6px',
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    color: '#ef4444',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#fef2f2';
                    e.currentTarget.style.borderColor = '#ef4444';
                    e.currentTarget.style.color = '#dc2626';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#ffffff';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.color = '#ef4444';
                    e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                  }}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </div>
      </div>

      {/* Comments List */}
      <div style={{ 
        flex: 1,
        overflowY: 'auto',
        padding: '20px 24px'
      }}>
        {comments && comments.length > 0 ? (
          comments.map((comment, index) => {
            const isCurrentUser = isAuthor(comment.author);
            const authorName = formatAuthor(comment.author);
            const messageDate = new Date(comment.createdAt);
            const messageDateStr = messageDate.toLocaleDateString('en-GB', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric' 
            });
            const messageTimeStr = messageDate.toLocaleTimeString('en-GB', { 
              hour: '2-digit', 
              minute: '2-digit' 
            });
            const messageDateTime = `${messageDateStr} ${messageTimeStr}`;
            
            
            
            return (
              <div 
                key={comment.refId || index} 
                style={{ 
                  display: 'flex',
                  justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
                  alignItems: 'flex-end',
                  gap: '8px',
                  position: 'relative',
                  marginBottom: '12px'
                }}
                onMouseEnter={() => isCurrentUser && setHoveredComment(comment.refId)}
                onMouseLeave={() => isCurrentUser && setHoveredComment(null)}
              >
                {/* Avatar for other users */}
                {!isCurrentUser && (
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: '#f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#666',
                    fontSize: '12px',
                    fontWeight: '500',
                    flexShrink: 0
                  }}>
                    {authorName.charAt(0).toUpperCase()}
                  </div>
                )}
                
                {/* Message bubble */}
                <div style={{ 
                  maxWidth: '70%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: isCurrentUser ? 'flex-end' : 'flex-start'
                }}>
                  {editingComment === comment.refId ? (
                    // Edit mode
                    <div style={{ 
                      padding: '12px 16px',
                      borderRadius: '16px',
                      background: '#ffffff',
                      border: '2px solid #3b82f6',
                      fontSize: '14px',
                      lineHeight: '1.4',
                      wordWrap: 'break-word',
                      position: 'relative',
                      boxShadow: '0 4px 16px rgba(59, 130, 246, 0.15)',
                      minWidth: '200px',
                      animation: 'fadeIn 0.2s ease-out'
                    }}>
                      <Input.TextArea
                        value={currentCommentEdit}
                        onChange={(e) => setCurrentCommentEdit(e.target.value)}
                        onPressEnter={() => handleSaveEditedComment(comment.refId)}
                        autoFocus
                        style={{
                          border: 'none',
                          background: 'transparent',
                          fontSize: '14px',
                          lineHeight: '1.4',
                          resize: 'none',
                          boxShadow: 'none',
                          padding: '0',
                          minHeight: '20px',
                          color: '#2d3748'
                        }}
                        autoSize={{ minRows: 1, maxRows: 4 }}
                      />
                      <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '8px',
                        marginTop: '12px',
                        paddingTop: '8px',
                        borderTop: '1px solid #f1f5f9'
                      }}>
                        <Button
                          size="small"
                          onClick={handleCancelEditedComment}
                          style={{ 
                            fontSize: '12px', 
                            height: '28px',
                            borderRadius: '6px',
                            fontWeight: '500',
                            color: '#6b7280',
                            border: '1px solid #e5e7eb'
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => handleSaveEditedComment(comment.refId)}
                          loading={isSavingComment}
                          style={{ 
                            fontSize: '12px', 
                            height: '28px',
                            borderRadius: '6px',
                            fontWeight: '500',
                            background: '#3b82f6',
                            borderColor: '#3b82f6'
                          }}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Display mode
                    <div style={{ 
                      padding: '12px 16px',
                      borderRadius: '16px',
                      background: isCurrentUser ? '#3b82f6' : '#f1f5f9',
                      color: isCurrentUser ? '#ffffff' : '#2d3748',
                      fontSize: '14px',
                      lineHeight: '1.4',
                      wordWrap: 'break-word',
                      position: 'relative',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                    }}>
                      {renderMarkdownLinks(comment.message)}
                    </div>
                  )}
                  
                  {/* Timestamp and author */}
                  <div style={{ 
                    fontSize: '11px', 
                    color: '#999',
                    marginTop: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    {!isCurrentUser && (
                      <span style={{ fontWeight: '500' }}>{authorName}</span>
                    )}
                    <span>{messageDateTime}</span>
                    {isCurrentUser && (
                      <span style={{ color: '#3b82f6' }}>•</span>
                    )}
                    {isCurrentUser && (
                      <span>Sent</span>
                    )}
                  </div>
                </div>
                
                {/* Hover actions for current user's messages */}
                {isCurrentUser && hoveredComment === comment.refId && editingComment !== comment.refId && (
                  <div style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    gap: '2px',
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '20px',
                    padding: '6px 8px',
                    zIndex: 10,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    backdropFilter: 'blur(8px)',
                    animation: 'fadeIn 0.2s ease-out'
                  }}>
                    <Tooltip title="Edit message">
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEditComment(comment)}
                        style={{ 
                          color: '#6b7280',
                          padding: '4px 6px',
                          height: '24px',
                          minWidth: '24px'
                        }}
                      />
                    </Tooltip>
                    <Popconfirm
                      title="Delete this message?"
                      onConfirm={() => handleDeleteComment(comment.refId)}
                      okText="Delete"
                      cancelText="Cancel"
                      okButtonProps={{ danger: true }}
                    >
                      <Tooltip title="Delete message">
                        <Button
                          type="text"
                          size="small"
                          icon={<DeleteOutlined />}
                          loading={deletingCommentId === comment.refId}
                          style={{ 
                            color: '#ef4444',
                            padding: '4px 6px',
                            height: '24px',
                            minWidth: '24px'
                          }}
                        />
                      </Tooltip>
                    </Popconfirm>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px', 
            color: '#6b7280',
            fontStyle: 'italic',
            background: '#f8fafc',
            borderRadius: '12px',
            border: '1px dashed #d1d5db'
          }}>
            No comments yet. Add a comment below to start the conversation.
          </div>
        )}
      </div>

      {/* Add Comment Section - Minimalist Design */}
      <div style={{ 
        borderTop: '1px solid #e2e8f0',
        padding: '20px 24px',
        background: '#ffffff'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center',
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '24px',
          padding: '4px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          transition: 'all 0.2s ease'
        }}>
          <Input.TextArea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Write your reply..."
            rows={1}
            autoSize={{ minRows: 1, maxRows: 4 }}
            style={{ 
              flex: 1,
              border: 'none',
              background: 'transparent',
              boxShadow: 'none',
              resize: 'none',
              fontSize: '14px',
              lineHeight: '1.4',
              padding: '8px 16px'
            }}
            onPressEnter={(e) => {
              if (e.shiftKey) return;
              e.preventDefault();
              if (newComment.trim()) {
                handleSaveComment();
              }
            }}
          />
          <Button 
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSaveComment}
            disabled={!newComment?.trim()}
            loading={isSavingComment}
            style={{ 
              height: '36px',
              width: '36px',
              minWidth: '36px',
              padding: '0',
              borderRadius: '18px',
              border: 'none',
              background: newComment?.trim() ? '#3b82f6' : '#e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              boxShadow: 'none'
            }}
          />
        </div>
      </div>

      {/* Resolution Modal */}
      {showResolveModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '12px',
            width: '400px',
            maxWidth: '90vw'
          }}>
            <div style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              marginBottom: '16px' 
            }}>
              Resolve Issue
            </div>
            <Form layout="vertical">
              <Form.Item label="Resolution Code">
                <Select
                  value={selectedResolutionCode}
                  onChange={setSelectedResolutionCode}
                  placeholder="Select resolution code"
                  style={{ width: '100%' }}
                >
                  {resolutionCodes.map(code => (
                    <Select.Option key={code} value={code}>
                      {code}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              {selectedResolutionCode === 'Other' && (
                <Form.Item label="Custom Resolution">
                  <Input
                    value={customResolutionCode}
                    onChange={(e) => setCustomResolutionCode(e.target.value)}
                    placeholder="Enter custom resolution"
                  />
                </Form.Item>
              )}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: '8px',
                marginTop: '16px'
              }}>
                <Button onClick={() => setShowResolveModal(false)}>
                  Cancel
                </Button>
                <Button 
                  type="primary" 
                  onClick={handleResolveWithCode}
                  loading={isResolving}
                >
                  Resolve
                </Button>
              </div>
            </Form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommentSystemContent;
